import { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import Select from "react-select";
import { toast, ToastContainer } from "react-toastify";
import adminCompanyCreditApi from "../../../../../api/adminCompanyCredit";
import companyApi from "../../../../../api/company";
import companyUserApi from "../../../../../api/companyUsers";
import creditTransferApi from "../../../../../api/creditTransferApi";

const TransferFrymes = ({ show, onClose, onSuccess }) => {
  const user = JSON.parse(localStorage.getItem("user"));
  const userEmail = user?.userEmail;

  const [myCompanies, setMyCompanies] = useState([]);
  const [assignedCompanies, setAssignedCompanies] = useState([]);
  
  const [fromCompany, setFromCompany] = useState("");
  const [targetType, setTargetType] = useState("myCompanies"); // 'myCompanies' or 'assignedCompanies'
  const [toCompany, setToCompany] = useState("");
  const [credits, setCredits] = useState("");
  const [loading, setLoading] = useState(false);
  const [companyCredits, setCompanyCredits] = useState(null);
  const [creditLoading, setCreditLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (show && userEmail) {
      fetchCompanies();
      // Reset state when modal opens
      setFromCompany("");
      setToCompany("");
      setCredits("");
      setTargetType("myCompanies");
      setCompanyCredits(null);
      setErrorMessage("");
    }
  }, [show, userEmail]);

  const fetchCompanies = async () => {
    try {
      // Fetch My Companies
      const myCompRes = await companyApi.getListOfCompanyByAdminEmail(userEmail);
      setMyCompanies(Array.isArray(myCompRes.data) ? myCompRes.data : []);

      // Fetch Assigned Companies
      const assignedCompRes = await companyUserApi.getAssignedCompaniesFromAdmin(userEmail);
      setAssignedCompanies(Array.isArray(assignedCompRes.data) ? assignedCompRes.data : []);
    } catch (error) {
      console.error("Failed to load companies", error);
      toast.error("Failed to load companies");
    }
  };

  const handleSourceCompanyChange = async (selected) => {
    const val = selected?.value || "";
    setFromCompany(val);
    if (val === toCompany) {
        setToCompany("");
    }
    setCompanyCredits(null);
    setErrorMessage("");
    setCredits("");

    if (val) {
      try {
        setCreditLoading(true);
        const res = await adminCompanyCreditApi.getCompanyCreditsByCompany(val);
        const creds = res.data;
        setCompanyCredits(creds);
        
        const free = creds?.freeCreidts || 0;
        const balance = creds?.balanceCredit || 0;
        const paid = creds?.paidcredits || 0;
        
        const transferable = balance - free;

        if (free === balance || paid <= 0 || transferable <= 0) {
            setErrorMessage("Only free frymes, no paid credits. You are not eligible for transfer because these are free frymes you got from signbook so you cannot.");
        }
      } catch (error) {
         console.error("Failed to load company credits", error);
      } finally {
        setCreditLoading(false);
      }
    }
  };

  const handleTransfer = async () => {
    try {
      setLoading(true);

      await creditTransferApi.transferCredits({
        transferType: "COMPANY",
        fromCompany,
        toCompany,
        credits: Number(credits),
      });

      toast.success("Frymes transferred successfully!");

      setCredits("");
      setToCompany("");
      setFromCompany("");
      setCompanyCredits(null);
      setErrorMessage("");

      if (onSuccess) {
        onSuccess();
      }

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.response?.data || "Failed to transfer frymes"
      );
    } finally {
      setLoading(false);
    }
  };

  // Options for From Company
  const fromOptions = myCompanies.map((c) => ({
    value: c.companyName,
    label: c.companyName,
  }));

  // Options for To Company based on target type
  const toOptions = (targetType === "myCompanies" ? myCompanies : assignedCompanies)
    .filter(c => c.companyName !== fromCompany) // cannot transfer to the same company
    .map((c) => ({
      value: c.companyName,
      label: c.companyName,
    }));

  return (
    <>
      <Modal show={show} onHide={onClose} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Transfer Frymes (Company to Company)</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Please select Source Company</Form.Label>
            <Select
              options={fromOptions}
              value={fromOptions.find((o) => o.value === fromCompany)}
              onChange={handleSourceCompanyChange}
              placeholder="Search source company..."
              isClearable
              isDisabled={creditLoading}
            />
            {creditLoading && <div className="mt-2 text-muted"><Spinner size="sm" animation="border" /> Fetching credits...</div>}
          </Form.Group>

          {companyCredits && (
            <div className="mb-3">
              <p style={{ fontSize: "14px" }}>
                <strong>Free frymes:</strong> {companyCredits?.freeCreidts || 0}
                &nbsp;||&nbsp;
                <strong>Total paid frymes:</strong>{" "}
                {companyCredits?.paidcredits || 0}
                &nbsp;||&nbsp;
                <strong>Balance paid frymes:</strong>{" "}
                {companyCredits?.balanceCredit || 0}
                &nbsp;||&nbsp;
                <strong>Used frymes:</strong> {companyCredits?.usedCredit || 0}
              </p>
              {errorMessage && <p className="text-danger" style={{ fontSize: "14px", fontWeight: "bold" }}>{errorMessage}</p>}
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Transfer Destination Type</Form.Label>
            <div>
              <Form.Check
                inline
                label="My Companies"
                name="targetType"
                type="radio"
                id="myCompanies"
                checked={targetType === "myCompanies"}
                onChange={() => {
                  setTargetType("myCompanies");
                  setToCompany("");
                }}
              />
              <Form.Check
                inline
                label="Assigned Companies"
                name="targetType"
                type="radio"
                id="assignedCompanies"
                checked={targetType === "assignedCompanies"}
                onChange={() => {
                  setTargetType("assignedCompanies");
                  setToCompany("");
                }}
              />
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Transfer To</Form.Label>
            <Select
              options={toOptions}
              value={toOptions.find((o) => o.value === toCompany)}
              onChange={(selected) => setToCompany(selected?.value || "")}
              placeholder="Search destination company..."
              isClearable
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Frymes to Transfer</Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={credits}
              onChange={(e) => setCredits(e.target.value)}
              disabled={!!errorMessage || !fromCompany || creditLoading}
            />
            {!errorMessage && companyCredits && (
               <Form.Text className="text-success">
                 You can transfer up to {(companyCredits?.balanceCredit || 0) - (companyCredits?.freeCreidts || 0)} frymes.
               </Form.Text>
            )}
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>

          <Button
            variant="success"
            onClick={handleTransfer}
            disabled={!fromCompany || !toCompany || !credits || loading || !!errorMessage || Number(credits) > ((companyCredits?.balanceCredit || 0) - (companyCredits?.freeCreidts || 0))}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Transferring...
              </>
            ) : (
              "Transfer"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
      />
    </>
  );
};

export default TransferFrymes;
